import { useState, useEffect, useRef, useMemo } from "react";
import { Trash2, Pencil, Plus, Camera, Soup, Utensils, Coffee, Leaf, Candy, X, Tag } from "lucide-react";
import { Button, Modal, Table, Select, TagFilter } from "../../../components";
import axios from "axios";
import { getServerUrl } from "../../../features/UrlHelper";
import { ALLERGENS } from "../../../features/orderConstants";
import { useAuth } from "../../../context/AuthContext";

const ITEM_TYPES = [
  { value: "SOUP",      label: "Polievka",     Icon: Soup },
  { value: "MENU",      label: "Hlavné jedlo", Icon: Utensils },
  { value: "DRINK",     label: "Nápoj",        Icon: Coffee },
  { value: "SIDE_DISH", label: "Príloha",      Icon: Leaf },
  { value: "DESSERT",   label: "Dezert",       Icon: Candy },
];

const typeInfo = (val) => ITEM_TYPES.find((t) => t.value === val) ?? ITEM_TYPES[1];

const itemTypeOptions = ITEM_TYPES.map(({ value, label, Icon }) => ({
  value,
  label: (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Icon size={15} />
      {label}
    </span>
  ),
}));

const EMPTY_FORM = {
  id: null,
  name: "",
  description: "",
  item_type: "MENU",
  allergens: [],
  photo: null,
  photoName: "",
  tags: [],
};

function MenuItemsSection() {
  const { userRole } = useAuth();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [existingPhoto, setExistingPhoto] = useState(null);
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [photoHovered, setPhotoHovered] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [topTags, setTopTags] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  // Build correct API endpoint based on user role
  const getMenuItemsBaseUrl = () => {
    const base = userRole === "MANAGER" ? "/api/manager/menu-items" : "/api/menu-items";
    return getServerUrl() + base;
  };

  const getMenuItemPhotoUrl = (id) => {
    const baseUrl = userRole === "MANAGER" ? "/api/manager/menu-items" : "/api/menu-items";
    return getServerUrl() + `${baseUrl}/${id}/photo`;
  };

  const getTagsUrl = (q) => {
    const baseUrl = userRole === "MANAGER" ? "/api/manager/tags" : "/api/tags";
    const base = getServerUrl() + baseUrl;
    return q ? `${base}?q=${encodeURIComponent(q)}` : base;
  };

  const getDeleteUrl = (id) => {
    const baseUrl = userRole === "MANAGER" ? "/api/manager/menu-items" : "/api/menu-items";
    return getServerUrl() + `${baseUrl}/${id}`;
  };

  const allTagsFromItems = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      if (Array.isArray(item.tags)) {
        item.tags.forEach((tag) => {
          if (!map.has(tag.id)) map.set(tag.id, tag);
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const filteredItems = useMemo(() => {
    let result = typeFilter ? items.filter((i) => i.item_type === typeFilter) : items;
    if (selectedFilterTags.length > 0) {
      result = result.filter((item) =>
        selectedFilterTags.every((tagId) =>
          Array.isArray(item.tags) && item.tags.some((t) => t.id === tagId)
        )
      );
    }
    return result;
  }, [items, typeFilter, selectedFilterTags]);


  useEffect(() => {
    loadItems();
    loadTopTags();
  }, [userRole]);

  async function loadTopTags() {
    try {
      const { data } = await axios.get(getTagsUrl());
      setTopTags(data);
    } catch (error) {
      console.error("Error fetching top tags:", error);
    }
  }

  async function loadItems() {
    try {
      const response = await axios.get(getMenuItemsBaseUrl());
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching menu items:", error);
    } finally {
      setLoadingItems(false);
    }
  }

  const openAdd = () => {
    setEditingItem(null);
    setExistingPhoto(null);
    setPhotoDeleted(false);
    setPhotoHovered(false);
    setTagInput("");
    setTagSuggestions([]);
    setShowSuggestions(false);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = async (item) => {
    setEditingItem(item);
    setExistingPhoto(null);
    setPhotoDeleted(false);
    setPhotoHovered(false);
    setTagInput("");
    setTagSuggestions([]);
    setShowSuggestions(false);
    setForm({
      id: item.id,
      name: item.name || "",
      description: item.description || "",
      item_type: item.item_type || "MENU",
      allergens: item.allergens_list
        ? item.allergens_list.split(",").map(Number).filter(Boolean)
        : [],
      photo: null,
      photoName: "",
      tags: Array.isArray(item.tags) ? item.tags : [],
    });
    setModalOpen(true);
    if (item.has_photo) {
      try {
        const res = await axios.get(getMenuItemPhotoUrl(item.id));
        setExistingPhoto(res.data.photo);
      } catch (err) {
        console.error("Failed to fetch item photo:", err);
      }
    }
  };

  const clearPhoto = () => {
    setForm((prev) => ({ ...prev, photo: null, photoName: "" }));
    setExistingPhoto(null);
    setPhotoDeleted(true);
    setPhotoHovered(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) =>
      setForm((prev) => ({ ...prev, photo: ev.target.result, photoName: file.name }));
    reader.readAsDataURL(file);
  };

  const toggleAllergen = (id) => {
    setForm((prev) => ({
      ...prev,
      allergens: prev.allergens.includes(id)
        ? prev.allergens.filter((a) => a !== id)
        : [...prev.allergens, id].sort((a, b) => a - b),
    }));
  };

  const handleTagInputChange = (e) => {
    const val = e.target.value;
    setTagInput(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setTagSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(getTagsUrl(val.trim()));
        setTagSuggestions(data);
        setShowSuggestions(true);
      } catch {
        // ignore
      }
    }, 250);
  };

  const addTag = (tag) => {
    if (form.tags.some((t) => t.name.toLowerCase() === tag.name.toLowerCase())) return;
    setForm((prev) => ({ ...prev, tags: [...prev.tags, { id: tag.id ?? null, name: tag.name }] }));
    setTagInput("");
    setTagSuggestions([]);
    setShowSuggestions(false);
  };

  const removeTag = (name) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t.name !== name) }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = tagInput.trim();
      if (!trimmed) return;
      const matched = tagSuggestions.find((t) => t.name.toLowerCase() === trimmed.toLowerCase());
      addTag(matched ?? { id: null, name: trimmed });
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const data = {
      id: form.id,
      name: form.name,
      description: form.description,
      item_type: form.item_type,
      allergens_list: form.allergens.length > 0 ? form.allergens.join(",") : null,
      tags: form.tags,
      ...(form.photo !== null
        ? { photo: form.photo }
        : photoDeleted
        ? { photo: null }
        : {}),
    };

    try {
      if (editingItem) {
        await axios.post(getMenuItemsBaseUrl(), data);
      } else {
        await axios.put(getMenuItemsBaseUrl(), data);
      }
      await loadItems();
      await loadTopTags();
      setModalOpen(false);
    } catch (err) {
      console.error("Failed to save menu item:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteItem = (id) => {
    axios
      .delete(getDeleteUrl(id))
      .then(() => setItems((prev) => prev.filter((i) => i.id !== id)))
      .catch((err) => console.error("Failed to delete menu item:", err));
    setDeleteConfirm(null);
  };

  return (
    <div className="admin__section">
      <div className="admin__sub-header">
        <h2 className="admin__section-title">Správa jedál</h2>
        <Button variant="primary" onClick={openAdd}>
          <Plus size={16} /> Pridať jedlo
        </Button>
      </div>

      {allTagsFromItems.length > 0 && (
        <div className="admin__tag-filter">
          <TagFilter
            availableTags={allTagsFromItems}
            topTags={topTags}
            selectedTagIds={selectedFilterTags}
            onChange={setSelectedFilterTags}
          />
        </div>
      )}

      <div className="admin__table-wrapper">
        <Table
          columns={[
            {
              key: "item_type",
              label: "Typ",
              render: (val) => {
                const { label, Icon } = typeInfo(val);
                return (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                    <Icon size={14} style={{ color: "var(--c-neutral-500)", flexShrink: 0 }} />
                    {label}
                  </span>
                );
              },
            },
            { key: "name", label: "Názov" },
            {
              key: "description",
              label: "Popis",
              hiddenOnMobile: true,
            },
            {
              key: "has_photo",
              label: "Foto",
              sortable: false,
              hiddenOnMobile: true,
              render: (val) =>
                val ? (
                  <Camera size={15} style={{ color: "var(--c-success)" }} />
                ) : (
                  <span style={{ color: "var(--c-neutral-400)" }}>—</span>
                ),
            },
            {
              key: "allergens_list",
              label: "Alergény",
              hiddenOnMobile: true,
              render: (val) =>
                val ? (
                  <span style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    {val.split(",").map((n) => (
                      <span
                        key={n}
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "1px 5px",
                          background: "var(--c-pozor-podklad-silny)",
                          color: "var(--c-pozor-text)",
                          borderRadius: 4,
                          lineHeight: 1.6,
                        }}
                      >
                        {n}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span style={{ color: "var(--c-neutral-400)" }}>—</span>
                ),
            },
            {
              key: "tags",
              label: "Tagy",
              hiddenOnMobile: true,
              sortable: false,
              render: (val) =>
                Array.isArray(val) && val.length > 0 ? (
                  <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {val.map((tag) => (
                      <span key={tag.id} className="admin__tag admin__tag--popular">
                        {tag.name}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span style={{ color: "var(--c-neutral-400)" }}>—</span>
                ),
            },
            {
              key: "actions",
              label: "Akcie",
              sortable: false,
              render: (_, row) => (
                <div className="admin__table-actions">
                  <button
                    className="admin__icon-btn"
                    onClick={() => openEdit(row)}
                    title="Upraviť"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    className="admin__icon-btn admin__icon-btn--danger"
                    onClick={() => setDeleteConfirm(row.id)}
                    title="Zmazať"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ),
            },
          ]}
          data={filteredItems}
          searchPlaceholder="Hľadať jedlo..."
          emptyMessage="Žiadne jedlá"
          toolbarContent={
            <Select
              value={typeFilter}
              onChange={(val) => setTypeFilter(val)}
              options={[
                { value: "", label: "Všetky typy" },
                ...ITEM_TYPES.map(({ value, label, Icon }) => ({
                  value,
                  label: (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Icon size={14} />
                      {label}
                    </span>
                  ),
                })),
              ]}
            />
          }
        />
      </div>

      {/* Add / Edit modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? "Upraviť jedlo" : "Pridať jedlo"}
        className="admin__modal--wide"
      >
        <form className="admin__form" onSubmit={handleSubmit}>
          <div className="admin__form-meal-grid">

            {/* ── Left column ── */}
            <div className="admin__form-meal-col">
              <div className="admin__form-field">
                <Select
                  label="Typ jedla"
                  options={itemTypeOptions}
                  value={form.item_type}
                  onChange={(val) => setForm((prev) => ({ ...prev, item_type: val }))}
                />
              </div>

              <div className="admin__form-field">
                <label className="admin__form-label">Názov</label>
                <input
                  maxLength={100}
                  className="admin__form-input"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="napr. Cesnaková polievka"
                  required
                />
              </div>

              <div className="admin__form-field">
                <label className="admin__form-label">Popis</label>
                <textarea
                  maxLength={255}
                  className="admin__form-input"
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Krátky popis jedla"
                  rows={4}
                  style={{ resize: "vertical", height: "auto", minHeight: 90 }}
                />
              </div>

              <div className="admin__form-field">
                <label className="admin__form-label">
                  Fotografia
                  {editingItem && !photoDeleted && " (ak nahráte novú, prepíše starú)"}
                  {editingItem && photoDeleted && (
                    <span style={{ color: "var(--c-error)", marginLeft: 6, fontWeight: 400 }}>
                      (fotka bude zmazaná)
                    </span>
                  )}
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera size={14} />
                    {form.photoName ? form.photoName : "Vybrať súbor"}
                  </Button>
                  {(form.photo || existingPhoto) && (
                    <div
                      style={{ position: "relative", display: "inline-block" }}
                      onMouseEnter={() => setPhotoHovered(true)}
                      onMouseLeave={() => setPhotoHovered(false)}
                    >
                      <img
                        src={form.photo ?? existingPhoto}
                        alt="náhľad"
                        style={{
                          width: 48,
                          height: 48,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid var(--c-neutral-300)",
                          cursor: "zoom-in",
                          display: "block",
                        }}
                      />
                      {photoHovered && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "calc(100% + 10px)",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 1000,
                            background: "var(--c-neutral-100)",
                            borderRadius: 10,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
                            border: "1px solid var(--c-neutral-300)",
                            overflow: "hidden",
                            pointerEvents: "none",
                          }}
                        >
                          <img
                            src={form.photo ?? existingPhoto}
                            alt="náhľad"
                            style={{ width: 220, height: 220, objectFit: "contain", display: "block" }}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={clearPhoto}
                        title="Zmazať fotku"
                        style={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          width: 18,
                          height: 18,
                          background: "var(--c-error)",
                          color: "var(--c-on-accent)",
                          border: "none",
                          borderRadius: "50%",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                          lineHeight: 1,
                        }}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
            </div>

            {/* ── Right column ── */}
            <div className="admin__form-meal-col">

              {/* Tags */}
              <div className="admin__form-field">
                <label className="admin__form-label">
                  <Tag size={11} style={{ marginRight: 4, verticalAlign: "middle" }} />
                  Tagy
                </label>

                {form.tags.length > 0 && (
                  <div className="admin__tag-list">
                    {form.tags.map((tag) => (
                      <span key={tag.name} className="admin__tag admin__tag--selected">
                        {tag.name}
                        <button
                          type="button"
                          className="admin__tag-remove"
                          onClick={() => removeTag(tag.name)}
                          aria-label={`Odstrániť tag ${tag.name}`}
                        >
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="admin__tag-input-wrapper">
                  <input
                    className="admin__form-input"
                    value={tagInput}
                    onChange={handleTagInputChange}
                    onKeyDown={handleTagKeyDown}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    onFocus={() => tagSuggestions.length > 0 && setShowSuggestions(true)}
                    placeholder="Napíš tag a stlač Enter..."
                    autoComplete="off"
                  />
                  {showSuggestions && tagSuggestions.length > 0 && (
                    <div className="admin__tag-suggestions">
                      {tagSuggestions.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          className="admin__tag-suggestion-item"
                          onMouseDown={() => addTag(tag)}
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {topTags.length > 0 && (
                  <div className="admin__tag-popular">
                    <span className="admin__tag-popular-label">Populárne:</span>
                    {topTags.map((tag) => {
                      const isSelected = form.tags.some((t) => t.name.toLowerCase() === tag.name.toLowerCase());
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          className={`admin__tag ${isSelected ? "admin__tag--selected" : "admin__tag--popular"}`}
                          onClick={() => !isSelected && addTag(tag)}
                          disabled={isSelected}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Allergens */}
              <div className="admin__form-field">
                <label className="admin__form-label">Alergény</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "6px 20px",
                    maxHeight: 260,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {ALLERGENS.map((a) => (
                    <label
                      key={a.id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 7,
                        cursor: "pointer",
                        fontSize: 13,
                        lineHeight: 1.4,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form.allergens.includes(a.id)}
                        onChange={() => toggleAllergen(a.id)}
                        style={{ marginTop: 2, accentColor: "var(--c-primary)", flexShrink: 0 }}
                      />
                      <span>
                        <strong>{a.id}.</strong> {a.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="admin__form-actions">
            <Button variant="ghost" type="button" onClick={() => setModalOpen(false)}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Ukladám..." : editingItem ? "Uložiť zmeny" : "Pridať jedlo"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Potvrdiť zmazanie"
      >
        <p>Naozaj chcete zmazať toto jedlo? Táto akcia sa nedá vrátiť.</p>
        <div className="admin__form-actions">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Zrušiť
          </Button>
          <Button
            variant="primary"
            onClick={() => deleteItem(deleteConfirm)}
            style={{ backgroundColor: "var(--c-error)", borderColor: "var(--c-error)" }}
          >
            Zmazať
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default MenuItemsSection;
